import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AppointmentDetailModal = ({ appointment, isAdmin, onClose, onReschedule, onCancel }) => {
  const supportTypeLabels = {
    strategy: 'Strategy Consultation',
    content: 'Content Planning',
    technical: 'Technical Support',
    training: 'Training Session'
  };

  const supportTypeIcons = {
    strategy: 'Target',
    content: 'FileText',
    technical: 'Settings',
    training: 'GraduationCap'
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const selectedPlatforms = appointment?.platforms
    ? Object.entries(appointment?.platforms)
        ?.filter(([_, value]) => value)
        ?.map(([key]) => key?.charAt(0)?.toUpperCase() + key?.slice(1))
        ?.join(', ')
    : 'None specified';

  return (
    <div className="fixed inset-0 z-200 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon 
                name={supportTypeIcons?.[appointment?.support_type] || 'Calendar'} 
                size={20} 
                color="var(--color-primary)" 
              />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {supportTypeLabels?.[appointment?.support_type] || appointment?.support_type}
              </h2>
              <p className="text-sm text-muted-foreground">
                Appointment Details
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} iconName="X" />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Date & Time */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <Icon name="Calendar" size={20} color="var(--color-primary)" />
              <h3 className="text-lg font-semibold text-foreground">Date & Time</h3>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Icon name="CalendarDays" size={16} color="var(--color-muted-foreground)" />
                <span className="text-foreground font-medium">{formatDate(appointment?.appointment_date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
                <span className="text-foreground font-medium">{appointment?.appointment_time} PST</span>
              </div>
            </div>
          </div>

          {/* Provider Information */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <Icon name="User" size={20} color="var(--color-primary)" />
              <h3 className="text-lg font-semibold text-foreground">Provider Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Full Name</p>
                <p className="text-foreground font-medium">{appointment?.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Email</p>
                <p className="text-foreground font-medium">{appointment?.email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Phone</p>
                <p className="text-foreground font-medium">{appointment?.phone}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Role</p>
                <p className="text-foreground font-medium">{appointment?.role}</p>
              </div>
            </div>
          </div>

          {/* Practice Information */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <Icon name="Building" size={20} color="var(--color-primary)" />
              <h3 className="text-lg font-semibold text-foreground">Practice Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Practice Name</p>
                <p className="text-foreground font-medium">{appointment?.practice_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Location</p>
                <p className="text-foreground font-medium">{appointment?.city}, {appointment?.state}</p>
              </div>
              {appointment?.website && (
                <div className="md:col-span-2">
                  <p className="text-sm text-muted-foreground mb-1">Website</p>
                  <a 
                    href={appointment?.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    {appointment?.website}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Consultation Details */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
              <h3 className="text-lg font-semibold text-foreground">Consultation Details</h3>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Marketing Goal</p>
                <p className="text-foreground">{appointment?.marketing_goal}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Current Marketing Effort</p>
                <p className="text-foreground">{appointment?.current_effort}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Active Platforms</p>
                <p className="text-foreground">{selectedPlatforms}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Challenges & Questions</p>
                <p className="text-foreground">{appointment?.challenges}</p>
              </div>
              {appointment?.additional_notes && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Additional Notes</p>
                  <p className="text-foreground">{appointment?.additional_notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Booking Metadata */}
          <div className="text-xs text-muted-foreground border-t border-border pt-4">
            <p>Booking ID: {appointment?.id}</p>
            <p>Created: {new Date(appointment?.created_at)?.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })} PST</p>
            {appointment?.updated_at !== appointment?.created_at && (
              <p>Last Updated: {new Date(appointment?.updated_at)?.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })} PST</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            variant="destructive"
            onClick={() => onCancel(appointment?.id)}
            iconName="Trash2"
          >
            Cancel Appointment
          </Button>
          <Button
            variant="primary"
            onClick={() => onReschedule(appointment)}
            iconName="Calendar"
          >
            Reschedule
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetailModal;